import {
  AppShell,
  Button,
  Group,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  reset,
  initialize,
  updateField,
} from "../slices/form/customOptionsFieldTab";
import {
  SaveCustomFieldRequest,
  useGetVentQuery,
  useSaveCustomFieldMutation,
} from "../slices/api/ventsApi";
import { useEffect, useState } from "react";
import {
  displayErrorMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { IsBlockNav, SetBlockNav } from "../App";
import { useLocation } from "wouter";
import { ConfigOptions, getCustomFieldIndex } from "../slices/types";
import { LabelWithTooltipHelp } from "../shared/components/LabelWithTooltipHelp";
import { LinkAnchorToVentHome } from "../shared/components/LinkAnchors";
import { EbTooltip } from "../shared/components/EbTooltip";
import { IconHelp } from "../shared/components/Icons";

interface Props {
  ventId: string;
  customFieldId: string;
  setTabDescription: (name: string) => void;
  setEventTabDescription: (name: string) => void;
}

const CustomFieldOptionsTab = (props: Props) => {
  const { ventId, customFieldId } = props;
  const [_] = useLocation();
  const { data: customFieldState, hasChanges } = useAppSelector(
    (state: RootState) => state.customOptionsFieldTab,
  );
  const {
    data: vent,
    isFetching,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({ id: ventId });
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (vent) {
      const customFieldIndex = getCustomFieldIndex(
        vent.customFields,
        customFieldId,
      );
      if (customFieldIndex < 0) {
        console.error("custom field not found");
        return;
      }
      const customField = vent.customFields[customFieldIndex];

      dispatch(initialize(customField));
      props.setTabDescription(customField.name);
      props.setEventTabDescription(vent.name);
      var optionsListText;
      if (customField.configOptions!.options) {
        optionsListText = customField.configOptions!.options.join(", ");
      } else {
        optionsListText = "";
      }
      setOptionsString(optionsListText);
    }
  }, [vent]);
  useEffect(() => {
    // During development useEffect is called twice by React.
    // We need to protect against that by returning if hasChanges didn't change
    if (IsBlockNav() === hasChanges) return;
    if (hasChanges) {
      SetBlockNav(true);
    } else {
      // Can happen if we support undo
      SetBlockNav(false);
    }
  }, [hasChanges]);

  const [saveCustomField, { isLoading: isSavingCustomField }] =
    useSaveCustomFieldMutation();
  const [optionsString, setOptionsString] = useState<string>("");

  const handleSave = async () => {
    if (customFieldState == null || customFieldState.configOptions == null) {
      console.error("customField is undefined but should not be");
      return;
    }
    if (customFieldState.name === "") {
      displayErrorMessage("Name is required.");
      return;
    }
    if (
      customFieldState.configOptions.options.length <
      customFieldState.configOptions.minOptions
    ) {
      displayErrorMessage(
        `You must provide at least ${customFieldState.configOptions.minOptions} options.`,
      );
      return;
    }
    if (
      customFieldState.configOptions.maxOptions > 0 &&
      customFieldState.configOptions.maxOptions <
        customFieldState.configOptions.minOptions
    ) {
      displayErrorMessage(
        `The maximum number of options must be greater than or equal to the minimum number of options.`,
      );
      return;
    }

    const request: SaveCustomFieldRequest = {
      id: customFieldId,
      name: customFieldState.name,
      description: customFieldState.description,
      hidden: customFieldState.hidden,
      configOptions: {
        minOptions: customFieldState.configOptions.minOptions,
        maxOptions: customFieldState.configOptions.maxOptions,
        options: customFieldState.configOptions.options,
      },
      ventId,
      updateTime: vent?.updateTime!,
    };

    try {
      await saveCustomField(request).unwrap();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };
  if (isErrorFetchingVent) {
    handleFetchingError(ventError);
    return null;
  }

  if (
    isFetching ||
    !vent ||
    !customFieldState ||
    customFieldState.configOptions == null
  ) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  return (
    <>
      <AppShell.Main>
        <Group justify="space-between" mb="xs" gap="0">
          <Stack gap="0">
            <Text mb="0" size="xl">
              {`Custom Field: ${customFieldState.name} (options field)`}
            </Text>
            <LinkAnchorToVentHome ventId={ventId}>
              {vent.name}
            </LinkAnchorToVentHome>
          </Stack>
        </Group>
        <Paper mt="lg">This field displays a list of options.</Paper>
        <TextInput
          style={{ width: "300px" }}
          maxLength={100}
          disabled={!vent.canUserEdit}
          required
          mt="md"
          label="Name"
          value={customFieldState.name ?? ""}
          onChange={(event) => {
            dispatch(
              updateField({
                field: "name",
                value: event.currentTarget.value,
              }),
            );
          }}
        />
        <TextInput
          style={{ width: "300px" }}
          maxLength={100}
          disabled={!vent.canUserEdit}
          mt="md"
          label="Description"
          value={customFieldState.description ?? ""}
          onChange={(event) => {
            dispatch(
              updateField({
                field: "description",
                value: event.currentTarget.value,
              }),
            );
          }}
        />
        <Group mt="md" gap="xs" align="center">
          <Switch
            disabled={!vent.canUserEdit}
            label="Hide"
            checked={customFieldState.hidden}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "hidden",
                  value: event.currentTarget.checked,
                }),
              )
            }
          />
          <EbTooltip label="Hiding a field will not delete its data.">
            <span
              aria-label="Help"
              style={{ display: "inline-flex", cursor: "help" }}
            >
              <IconHelp />
            </span>
          </EbTooltip>
        </Group>
        <Textarea
          disabled={!vent.canUserEdit}
          mt="sm"
          maxLength={200}
          style={{ width: "300px" }}
          rows={3}
          label="List of options (separate with commas)"
          value={optionsString}
          onChange={(event) => {
            setOptionsString(event.currentTarget.value);
            let optionsArray: string[] = [];
            const commaSeparatedText = event.currentTarget.value;
            if (commaSeparatedText != null && commaSeparatedText.length > 0) {
              optionsArray = commaSeparatedText.split(",");
            }
            const value = optionsArray.map((option) => option.trim());
            const co: ConfigOptions = {
              ...customFieldState.configOptions,
              options: value,
            } as ConfigOptions;
            dispatch(
              updateField({
                field: "configOptions",
                value: co,
              }),
            );
          }}
        />

        <div style={{ width: "300px", marginTop: "16px" }}>
          <LabelWithTooltipHelp
            helpText="Set to zero to make replying optional. The minimum is not enforced unless a guest changes their RSVP after accepting the invitation."
            labelText="Guest must choose at least..."
          />
          <NumberInput
            disabled={!vent.canUserEdit}
            min={0}
            maxLength={3}
            mt="xs"
            value={customFieldState.configOptions.minOptions}
            onChange={(value) => {
              const co: ConfigOptions = {
                ...customFieldState.configOptions,
                minOptions: value === "" ? 0 : value,
              } as ConfigOptions;
              dispatch(
                updateField({
                  field: "configOptions",
                  value: co,
                }),
              );
            }}
          />
        </div>

        <div style={{ width: "300px", marginTop: "16px" }}>
          <LabelWithTooltipHelp
            helpText="Set to limit the number of options guests may select. Leave blank if any number of options may be selected."
            labelText="Guest may choose at most..."
          />
          <NumberInput
            disabled={!vent.canUserEdit}
            min={0}
            maxLength={3}
            placeholder="all options may be selected"
            mt="xs"
            value={
              customFieldState.configOptions.maxOptions === 0
                ? ""
                : customFieldState.configOptions.maxOptions
            }
            onChange={(value) => {
              const co: ConfigOptions = {
                ...customFieldState.configOptions,
                maxOptions: value === "" ? 0 : value,
              } as ConfigOptions;
              dispatch(
                updateField({
                  field: "configOptions",
                  value: co,
                }),
              );
            }}
          />
        </div>
        <div>
          <Button
            mt="xs"
            mr="md"
            onClick={() => dispatch(reset())}
            disabled={!hasChanges}
          >
            Discard Changes
          </Button>
          <Button
            mt="xs"
            disabled={!hasChanges || !vent.canUserEdit || isSavingCustomField}
            onClick={handleSave}
            rightSection={isSavingCustomField ? <Loader size={14} /> : null}
          >
            Save Custom Field
          </Button>
        </div>
      </AppShell.Main>
    </>
  );
};

export default CustomFieldOptionsTab;
